import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { DriverProfile } from "@tdelivr/api-client/generated/driver/driver-profile.js";

import { boundedNote, PER_FETCH, type BoardRow } from "../board.ts";
import {
  APPROVED_BY_OPERATOR,
  ASSIGNMENT_WRITE_SCOPE,
  DECISION_SCOPE,
  decidableDocument,
  decisionKey,
  DOCUMENT_READ_SCOPE,
  DRIVER_READ_SCOPE,
  driverRow,
  rejectionRefusal,
  statesPresent,
  VEHICLE_CREATE_SCOPE,
  vehicleRefusal,
  type DocumentState,
  type VehicleDraft,
  type VehicleState,
} from "../driver-onboarding.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * Driver onboarding: the tenant's couriers, their paperwork, and the decision that lets them work.
 *
 * **This is the screen Q-236 is about.** Document 01 §265 says a tenant must be able to "manage
 * drivers"; DRV-03 publishes a verification decision under `driver-document:verify`; and no screen
 * on the platform spent that scope, so under ADR 0032 no client held it. The consequence was that
 * EXP-003's readiness gate blocked every courier on `no_documents`, so nobody could go online,
 * accept an offer or run a trip — on a deployment where every other surface worked.
 *
 * `src/driver-onboarding.ts` carries the reasoning about what a row means and why this screen has
 * no design file. The short version: there is none, that absence is Q-236, and this applies the
 * console's own conventions rather than inventing a second visual language. It sits outside the
 * screen ratio for the same reason the four analytics boards do.
 *
 * ## Two reads per board, and why the second one is per driver
 *
 * DRV-01 lists profiles for a tenant. DRV-03 publishes `GET /v1/drivers/{driverId}/documents` and
 * **no tenant-wide listing of documents**, so the paperwork column costs one read per driver. That
 * is an N+1 and it is the only shape available; the dispatch console's deliveries board records the
 * same trade for the same reason. It is bounded by the profile page, so the worst case is
 * `PER_FETCH` reads and not an unbounded fan-out, and a driver whose read fails draws `Not read`
 * rather than being dropped or shown as having nothing.
 *
 * ## The decision is the first command this console actually issues
 *
 * Every other primary on this console is a disabled button carrying the reason its command cannot
 * be served. This one approves the oldest pending document of the selected driver, which is the
 * narrowest useful action: a decision is per document, the board is per driver, and approving "the
 * one that is waiting" is what an administrator means when a courier has one licence pending. A
 * driver with several pending documents needs a row-level control the design has not drawn, so the
 * board approves one and says so in its footnote rather than looping silently.
 */

const profiles = services.driver__driver_profile;
const documents = services.driver__driver_document;
const vehicles = services.driver__vehicle;

export interface DriverOnboardingProps {
  readonly transport: Transport;
  /**
   * The scopes this session carries.
   *
   * Read rather than assumed: the vehicle commands are new scopes, and a token minted before
   * the realm was reconciled does not carry them. A button that fails is worse than one that
   * says which permission it is short of.
   */
  readonly scopes: ReadonlySet<string>;
  /*
   * **No `tenantId`, unlike every sibling board on this console.**
   *
   * Not an omission. DRV-01 addresses its listing as `GET /v1/driver-profiles` and takes the
   * tenant from the verified token, where IAM-02 addresses the staff listing as
   * `GET /v1/tenants/{tenantId}/workforce-members` and refuses a path naming another tenant with a
   * 403. Both are tenant-bounded; they differ in where the bound is written. A prop this component
   * accepted and never sent would read as though the caller chose the tenant, which is the thing
   * `apps/README.md` forbids a component from doing.
   */
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly profiles: readonly DriverProfile[];
  readonly truncated: boolean;
  /** Documents per driver, or `"unread"` where DRV-03 did not answer for that one. */
  readonly paperwork: ReadonlyMap<string, readonly DocumentState[] | "unread">;
  /** The courier's current vehicle, from DRV-04's by-driver assignment read. */
  readonly vehicles: ReadonlyMap<string, VehicleState>;
}

export function DriverOnboarding({ transport, scopes, crumbs }: DriverOnboardingProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  /** Why a rejection cannot be sent yet, or undefined when it can. Computed once, used twice. */
  const rejectRefusal = rejectionRefusal(rejectReason);
  const [outcome, setOutcome] = useState<string | undefined>(undefined);
  const [formOpen, setFormOpen] = useState(false);
  const [formFailure, setFormFailure] = useState<string | undefined>(undefined);
  const [vehicle, setVehicle] = useState<VehicleDraft>({
    registrationNumber: "",
    vehicleClass: "",
  });
  /** The courier a vehicle is being registered for. Set when the form opens. */
  const [vehicleFor, setVehicleFor] = useState<DriverProfile | undefined>(undefined);

  const load = useCallback(async () => {
    const profileClient = profiles.createDriverProfileClient(transport);
    const documentClient = documents.createDriverDocumentClient(transport);
    const vehicleClient = vehicles.createVehicleClient(transport);
    try {
      const answered = await profileClient.listDriverProfiles(
        undefined,
        String(PER_FETCH),
        undefined,
      );

      /*
       * Settled, not all-or-nothing. One driver's documents failing must not cost the board every
       * other driver's — the same rule EXP-003 applies to its seven layers, and for the same
       * reason: a screen that answers nothing because one source was unreachable is worse than one
       * that says which source it is.
       */
      const reads = await Promise.allSettled(
        answered.driverProfiles.map(async (profile) => {
          const page = await documentClient.getDriversDocuments(profile.driverId);
          return [profile.driverId, page.documents satisfies readonly DocumentState[]] as const;
        }),
      );

      const paperwork = new Map<string, readonly DocumentState[] | "unread">();
      for (const [index, read] of reads.entries()) {
        const driverId = answered.driverProfiles[index]?.driverId;
        if (driverId === undefined) continue;
        paperwork.set(driverId, read.status === "fulfilled" ? read.value[1] : "unread");
      }

      /*
       * The vehicle, from the by-driver assignment read R-09 was once recorded as not existing.
       * The newest **active** assignment, because an ended one is a vehicle the courier gave
       * back — showing it would tell an operator a courier is equipped when they are not.
       */
      const vehicleReads = await Promise.allSettled(
        answered.driverProfiles.map(async (profile) => {
          const page = await vehicleClient.listDriverVehicleAssignments(profile.driverId);
          const current = page.assignments.find((assignment) => assignment.status === "active");
          return [
            profile.driverId,
            current === undefined ? "none" : { registration: current.vehicle.registrationNumber },
          ] as const;
        }),
      );
      const assigned = new Map<string, VehicleState>();
      for (const [index, read] of vehicleReads.entries()) {
        const driverId = answered.driverProfiles[index]?.driverId;
        if (driverId === undefined) continue;
        assigned.set(driverId, read.status === "fulfilled" ? read.value[1] : "unread");
      }

      setLoaded({
        profiles: answered.driverProfiles,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
        paperwork,
        vehicles: assigned,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, [DRIVER_READ_SCOPE, DOCUMENT_READ_SCOPE]));
      setLoaded(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: readonly BoardRow[] | undefined = useMemo(() => {
    if (loaded === undefined) return undefined;
    const observedAt = Date.now();
    const all = loaded.profiles.map((profile) =>
      driverRow(
        profile,
        loaded.paperwork.get(profile.driverId) ?? "unread",
        observedAt,
        loaded.vehicles.get(profile.driverId) ?? "unread",
      ),
    );
    return state === "" ? all : all.filter((row) => row.state.label === state);
  }, [loaded, state]);

  /** The driver the command would act on: the selected one, or the only one waiting. */
  const target = useMemo(() => {
    if (loaded === undefined) return undefined;
    const waiting = loaded.profiles.filter((profile) => {
      const paperwork = loaded.paperwork.get(profile.driverId);
      return (
        paperwork !== undefined &&
        paperwork !== "unread" &&
        decidableDocument(paperwork) !== undefined
      );
    });
    if (selected !== undefined) {
      return waiting.find((profile) => profile.driverId === selected);
    }
    return waiting.length === 1 ? waiting[0] : undefined;
  }, [loaded, selected]);

  /**
   * Append a verification decision, which is the one command this board makes twice.
   *
   * Approving and rejecting are **the same operation** — DRV-03 publishes one
   * `verification-decisions` append whose `decision` is `approved` or `rejected`, and requires a
   * `reasonCode` on both. So this is one function taking the decision and its reason rather than
   * two that differ by a string, which is what keeps the optimistic-concurrency handling, the
   * scope, the idempotency key and the reload identical across the two.
   *
   * The reason is where they genuinely differ. An approval's is a constant, because an operator
   * approving says nothing a record needs beyond who and when. A rejection's is typed, because
   * the courier is going to be told it: a board that could only approve would teach that
   * rejection is not a normal outcome, and a board that rejects with a constant would leave no
   * record of why — and DRV-03 keeps every decision, immutably, precisely so that it does.
   */
  const decide = useCallback(
    (decision: "approved" | "rejected", reasonCode: string) => {
      if (loaded === undefined || target === undefined) return;
      const paperwork = loaded.paperwork.get(target.driverId);
      if (paperwork === undefined || paperwork === "unread") return;
      const document = decidableDocument(paperwork);
      if (document === undefined) return;

      setBusy(true);
      setOutcome(undefined);
      const client = documents.createDriverDocumentClient(transport);
      client
        .postDriversDocumentsVerificationDecisions(
          target.driverId,
          document.id,
          {
            decision,
            reasonCode,
            /*
             * The version read with the document. DRV-03 requires it, which makes the decision
             * refuse rather than overwrite when somebody else decided first — so two
             * administrators reviewing the same courier cannot both decide the same paper.
             */
            expectedVersion: document.version,
          },
          /*
           * The decision is in the key, not only the document. Approving and then rejecting the
           * same paper are two different commands, and a key naming only the document would make
           * the second one replay the first — which would report a rejection that never happened.
           */
          {
            idempotencyKey: decisionKey(decision, target.driverId, document.id),
          },
        )
        .then(() => {
          setOutcome(
            decision === "approved"
              ? `Approved ${document.type} for ${target.displayName}.`
              : `Rejected ${document.type} for ${target.displayName}. They can upload another.`,
          );
          setSelected(undefined);
          setRejectOpen(false);
          setRejectReason("");
          return load();
        })
        .catch((error: unknown) => {
          setFailure(stateForError(error, [DECISION_SCOPE]));
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [loaded, target, transport, load],
  );

  /** Approve, whose reason is a constant: who and when is the whole of what a record needs. */
  const approve = useCallback(() => {
    decide("approved", APPROVED_BY_OPERATOR);
  }, [decide]);

  /**
   * Register a vehicle and put this courier on it, in one press.
   *
   * Two operations, because DRV-04 models two: a vehicle exists, and an assignment ties it to a
   * courier. They are not offered as two presses because **DRV-04 publishes no vehicle listing**
   * — a vehicle created and not immediately assigned is one nothing on the platform can find
   * again, so leaving an operator between the steps would let them strand a record.
   *
   * If the assignment fails after the vehicle was created, that is exactly what has happened and
   * the message says so rather than implying nothing was written. Pressing again with the same
   * registration replays the create through DRV-04's own idempotency store and retries the
   * assignment.
   */
  const assignVehicle = useCallback(() => {
    if (vehicleFor === undefined) return;
    const refusal = vehicleRefusal(vehicle);
    if (refusal !== undefined) {
      setFormFailure(refusal);
      return;
    }

    setBusy(true);
    setFormFailure(undefined);
    const client = vehicles.createVehicleClient(transport);
    const registration = vehicle.registrationNumber.trim();

    client
      .createVehicle(
        {
          registrationNumber: registration,
          vehicleClass: vehicle.vehicleClass.trim(),
          /*
           * Explicit, and the explanation matters more than the two values.
           *
           * DRV-04 defaults a new vehicle to `inactive` and `draft`, and EXP-003 passes its gate
           * only on `active` + `active_record` — so a vehicle created at the defaults is one the
           * courier still cannot go online with, which would make this command look like it had
           * worked and change nothing.
           *
           * The longer lifecycle DRV-04 models — `submitted`, `under_review` — has no screen
           * anywhere and no operation of its own: unlike MER-01, which publishes a status change
           * carrying a reason, DRV-04 exposes the lifecycle as a field on a generic PATCH. A
           * service that models review as its own command is asking for two actors; one that models
           * it as a field is not. So the operator registering a vehicle for their own courier is
           * the person vouching for it, and the board says so in its footnote rather than
           * performing a review nobody designed.
           */
          operationalStatus: "active",
          lifecycleStatus: "active_record",
        },
        { idempotencyKey: `tenant-admin/vehicle/${registration}` },
      )
      .then(async (created) => {
        await client.assignVehicle(
          created.vehicle.id,
          {
            driverId: vehicleFor.driverId,
            /*
             * The courier's identity subject, which DRV-01 holds as `userId`. DRV-04 stores it
             * so its own self-scoped reads can compare a token's subject against it without
             * asking DRV-01 — which is what lets a courier read their own vehicle and nobody
             * else's.
             */
            driverUserId: vehicleFor.userId,
            assignmentSource: "fleet_assigned",
            effectiveFrom: new Date().toISOString(),
          },
          {
            idempotencyKey: `tenant-admin/assign/${created.vehicle.id}/${vehicleFor.driverId}`,
          },
        );
        setOutcome(`${registration} is assigned to ${vehicleFor.displayName}.`);
        setFormOpen(false);
        setVehicle({ registrationNumber: "", vehicleClass: "" });
        setVehicleFor(undefined);
        return load();
      })
      .catch((error: unknown) => {
        setFormFailure(
          error instanceof Error
            ? `${error.message} The vehicle may already exist; pressing again with the same registration retries the assignment rather than creating a second one.`
            : "DRV-04 refused the vehicle.",
        );
      })
      .finally(() => {
        setBusy(false);
      });
  }, [vehicle, vehicleFor, transport, load]);

  /**
   * The courier most in need of a vehicle: cleared paperwork and nothing to drive.
   *
   * Offered unprompted for the same reason the merchants board offers a branch: this courier's
   * row looks finished and they still cannot go online, and that is the one consequence on this
   * board an operator cannot see.
   */
  const unequipped = useMemo(() => {
    if (loaded === undefined) return undefined;
    /*
     * Any courier with no vehicle, whatever their paperwork says.
     *
     * The first version of this required cleared documents too, and that coupling was invented
     * here rather than taken from anywhere. EXP-003 holds documents and vehicle as **independent
     * gates** — a courier passes or fails each on its own — and an operator equipping a fleet has
     * no reason to wait on a review. Requiring both also made the command unreachable on a tenant
     * whose couriers had uploaded nothing, which is every tenant until somebody uploads something.
     *
     * `"none"` and not `!== undefined`: a driver whose assignment read did not answer is
     * `"unread"`, and offering a vehicle to a courier who may already have one would create a
     * second assignment on a guess.
     */
    return loaded.profiles.find((profile) => loaded.vehicles.get(profile.driverId) === "none");
  }, [loaded]);

  const bounded = boundedNote(
    loaded?.profiles.length,
    loaded?.truncated ?? false,
    "drivers",
    "newest first",
  );

  /** Why the command cannot be pressed, where that is a fact about the rows rather than a scope. */
  const unavailable =
    loaded === undefined
      ? "The board has not loaded yet."
      : target === undefined
        ? "Select a driver whose paperwork is awaiting review. No document is waiting, or more than one driver has one."
        : undefined;

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Driver onboarding"
      source="DRV-01 · DRV-03"
      description="Your couriers and whether their documents let them work."
      columns={["Driver", "Paperwork", "Vehicle"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="A courier appears here as soon as a driver record exists for them."
      primary={{
        label: "Approve waiting document",
        onPress: approve,
        busy,
        ...(unavailable === undefined ? {} : { unavailable }),
      }}
      /*
       * Rejecting, which is the same DRV-03 operation with the other decision and a reason the
       * operator types. Secondary rather than primary because approving is the common outcome,
       * and present at all because a board that can only approve teaches that rejection is not a
       * normal one — the courier's own screen already says *“We're checking your documents”*, and
       * what it cannot say is why a check failed.
       *
       * It opens the form rather than firing, for the same reason the trips board confirms a
       * cancellation: a decision DRV-03 keeps immutably should not be one press away.
       */
      {...(unavailable === undefined
        ? {
            secondary: {
              label: "Reject with a reason",
              onPress: (): void => {
                setRejectOpen(true);
                setFormOpen(false);
              },
              busy,
            },
          }
        : {})}
      {...(rejectOpen
        ? {
            form: {
              open: true,
              title: `Reject ${target?.displayName ?? "this courier"}'s document`,
              // The page's own rail glyph, not a new one: neither of this board's two
              // forms is in the design's FORMS table. Q-288.
              icon: "badge",
              legend: "Rejection",
              fields: [
                {
                  name: "reasonCode",
                  label: "Reason",
                  value: rejectReason,
                  onChange: (value: string): void => {
                    setRejectReason(value);
                  },
                  /*
                   * Free text, because DRV-03 types `reasonCode` as a string and publishes no
                   * vocabulary for it. A console offering a fixed list would be inventing one,
                   * and the list it invented would be the one every tenant was stuck with.
                   */
                  hint: "The courier is told this. DRV-03 keeps it on the decision, which cannot be edited later.",
                },
              ],
              submitLabel: "Reject the document",
              /*
               * The empty reason is refused here rather than sent and refused by DRV-03.
               *
               * The first version of this put the refusal on a `refusal` key the form does not
               * have — the field is `failure` — and **a conditional spread hid it from the
               * typechecker**, which is the same silence a cast gives: an excess property inside
               * `...(cond ? {x} : {})` is not checked against the target. It compiled, it would
               * have rendered nothing, and the only symptom would have been a rejection sent with
               * an empty reason. Stated as a plain guard instead, where it is checked.
               */
              onSubmit: (): void => {
                if (rejectRefusal !== undefined) return;
                const reason = rejectReason.trim();
                decide("rejected", reason);
              },
              onCancel: (): void => {
                setRejectOpen(false);
                setRejectReason("");
              },
              busy,
              /*
               * Computed once and narrowed, rather than called twice with a cast on the second.
               * `exactOptionalPropertyTypes` is why a conditional spread is needed at all here, and
               * a cast inside it would be the second silence this form has already had — the first
               * put the refusal on a key the form does not have, and a spread hid it from the
               * typechecker.
               */
              ...(rejectRefusal === undefined ? {} : { failure: rejectRefusal }),
            },
          }
        : {})}

      {...(formOpen
        ? {
            form: {
              open: true,
              title: `Register a vehicle for ${vehicleFor?.displayName ?? "this courier"}`,
              icon: "badge",
              legend: "Vehicle",
              fields: [
                {
                  name: "registrationNumber",
                  label: "Registration",
                  value: vehicle.registrationNumber,
                  onChange: (value: string): void => {
                    setVehicle((current) => ({ ...current, registrationNumber: value }));
                  },
                  hint: "The plate, as it appears on the vehicle.",
                },
                {
                  name: "vehicleClass",
                  label: "Class",
                  value: vehicle.vehicleClass,
                  onChange: (value: string): void => {
                    setVehicle((current) => ({ ...current, vehicleClass: value }));
                  },
                  /*
                   * Free text, not a list of two. DRV-04 publishes no vocabulary for this, and
                   * a console that offered only the two kinds the design happens to draw would
                   * refuse a tenant running anything else.
                   */
                  hint: "Such as motorcycle or car. The design draws those two; the service accepts any.",
                },
              ],
              submitLabel: "Register and assign",
              onSubmit: assignVehicle,
              onCancel: (): void => {
                setFormOpen(false);
                setVehicleFor(undefined);
                setFormFailure(undefined);
              },
              busy,
              ...(formFailure === undefined ? {} : { failure: formFailure }),
            },
          }
        : {})}
      {...(unequipped !== undefined &&
      scopes.has(VEHICLE_CREATE_SCOPE) &&
      scopes.has(ASSIGNMENT_WRITE_SCOPE)
        ? {
            secondary: {
              label: `Give ${unequipped.displayName} a vehicle`,
              onPress: (): void => {
                setOutcome(undefined);
                setFormFailure(undefined);
                setVehicleFor(unequipped);
                setFormOpen(true);
              },
              busy,
            },
          }
        : {})}
      filter={{
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{
        /*
         * A `warn`, not an `info`. The sentence is about something a courier cannot do, and the
         * console's own vocabulary reserves `warn` for a state that holds work up.
         */
        kind: "warn",
        glyph: "warning",
        text:
          outcome ??
          "A courier cannot enter the dispatch pool until every uploaded document is approved and none has expired.",
      }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote="No design file draws this board — that absence is Q-236 in docs/design/DESIGN_QUESTIONS.md — so its words and layout are this console's own conventions rather than a design's, and it sits outside the screen ratio as the analytics boards do. The paperwork column costs one DRV-03 read per driver because DRV-03 publishes no tenant-wide listing of documents; a driver whose read did not answer reads Not read rather than being shown as having none. A vehicle registered here is created active and road-ready in one press: DRV-04 defaults a new vehicle to inactive and draft, EXP-003 passes its gate only on active and active_record, and the lifecycle in between — submitted, under_review — has no screen and no operation of its own, only a field on a generic PATCH. So the operator registering a vehicle for their own courier is the person vouching for it. The command approves the one document that is waiting, so a courier with several pending papers needs it pressed once each — a row-level control is what the design would add."
      exportName="driver-onboarding"
      detailSubtitle="Driver onboarding · DRV-01 · DRV-03"
      detailNote="A courier cannot enter the dispatch pool until every uploaded document is approved and none has expired."
    />
  );
}
